/* ==========================================================================
   Single source of truth for site content.
   Editing copy, products or company details should only ever happen here.
   ========================================================================== */

export const company = {
    legalName: 'Kifayti Retail Private Limited',
    brand: 'Kifayti',
    tagline: 'Simple, Sasta aur Superfast',
    email: 'kifaytiranchi@gmail.com',
    phone: '+91 8935965695',
    phoneRaw: '918935965695',
    address: {
        line1: 'Krishna Nagar',
        line2: 'Bagodar, Jharkhand 825322',
        country: 'India',
    },
    gst: '20AAJCK1352M1ZN',
    cin: 'U01100JH2021PTC017551',
    founded: '2021',
    social: {
        facebook: 'https://www.facebook.com/p/Kifayti-100083522170468/',
        instagram: 'https://www.instagram.com/kifayti.in/',
        whatsapp: 'https://wa.me/918935965695',
    },
};

export type Product = {
    slug: string;
    name: string;
    category: string;
    url: string;
    tagline: string;
    description: string;
    features: string[];
    audience: string;
    accent: string;
    icon: string;
    status: 'live' | 'upcoming';
};

export const products: Product[] = [
    {
        slug: 'feeisy',
        name: 'Feeisy',
        category: 'School ERP',
        url: 'https://feeisy.com/',
        tagline: 'Modern school ERP for Indian schools',
        description:
            'Cloud-based school ERP for managing students, fee collection, transport, attendance and parent communication — affordable, mobile-friendly and quick to roll out.',
        features: [
            'Student records & admissions',
            'Fee collection and receipts',
            'Attendance tracking',
            'Transport management',
            'Parent communication',
            'Invoices & reporting',
        ],
        audience: 'Schools, coaching centres and institutes',
        accent: '#6366f1',
        icon: '🎓',
        status: 'live',
    },
    {
        slug: 'pagarisy',
        name: 'PAGARisy',
        category: 'Payroll & HRMS',
        url: 'https://pagarisy.com/',
        tagline: 'Payroll and attendance, one dashboard',
        description:
            'Payroll, GPS and selfie attendance, leave rules and salary-linked advances in a single dashboard built for Indian SMBs, schools and startups.',
        features: [
            'Payroll processing',
            'GPS & selfie attendance',
            'Leave rules and approvals',
            'Salary advances',
            'Employee records',
            'Payslips & compliance reports',
        ],
        audience: 'SMBs, schools and startups',
        accent: '#a855f7',
        icon: '💼',
        status: 'live',
    },
    {
        slug: 'promoisy',
        name: 'Promoisy',
        category: 'Digital Signage',
        url: 'https://promoisy.com/',
        tagline: 'Turn any screen into a display network',
        description:
            'Digital signage made simple. Upload once and play everywhere — manage every promotional screen across locations from a single browser tab, with no hardware expertise.',
        features: [
            'Android TV & tablet kiosk app',
            'Images and video playlists',
            'Multi-location management',
            'Scheduling and auto-expiry',
            'Footer ticker messages',
            'Playback analytics',
        ],
        audience: 'Retail, restaurants, hotels and gyms',
        accent: '#22d3ee',
        icon: '📺',
        status: 'live',
    },
    {
        slug: 'quotationisy',
        name: 'QUOTATIONisy',
        category: 'Quotations & Invoicing',
        url: 'https://quotationisy.com/',
        tagline: 'Professional quotations in minutes',
        description:
            'Quotation and invoice management for small businesses — build professional quotes, convert them to invoices, and keep every client document in one organised place.',
        features: [
            'Quotation builder',
            'Quote-to-invoice conversion',
            'Client and item catalogue',
            'Branded PDF documents',
            'Payment tracking',
            'Document history',
        ],
        audience: 'Small businesses, agencies and traders',
        accent: '#fbbf24',
        icon: '🧾',
        status: 'live',
    },
    {
        slug: 'fixisy',
        name: 'Fixisy',
        category: 'Repair & Service Ops',
        url: 'https://fixisy.com/',
        tagline: 'All-in-one repair management',
        description:
            'Repair management for service centres and their customers — track repairs, manage dues, handle payments, broadcast offers and run reports from any device.',
        features: [
            'Repair job tracking',
            'Due management',
            'Technician workflows',
            'Inventory control',
            'Customer app & updates',
            'Reports and cloud backup',
        ],
        audience: 'Service centres and repair businesses',
        accent: '#34d399',
        icon: '🔧',
        status: 'live',
    },
];

export const upcomingNote = {
    title: 'More platforms in build',
    description:
        'The "-isy" family keeps growing. New products for billing, logistics and customer engagement are in active development and roll out to existing clients first.',
};

export type Service = {
    title: string;
    description: string;
    points: string[];
    icon: string;
};

export const services: Service[] = [
    {
        title: 'School & Institute Digitalization',
        icon: '🏫',
        description:
            'End-to-end digital transformation for schools — admissions, fees, attendance, payroll and parent communication moved onto one secure cloud system.',
        points: [
            'ERP setup and data migration',
            'Online fee collection',
            'Staff payroll and attendance',
            'Parent apps and notifications',
        ],
    },
    {
        title: 'Business & Organization Systems',
        icon: '🏢',
        description:
            'Operational software for organizations that still run on registers and spreadsheets, replaced with role-based dashboards and reliable reporting.',
        points: [
            'Custom ERP and admin portals',
            'Role-based access control',
            'Real-time operational dashboards',
            'Reporting and exports',
        ],
    },
    {
        title: 'Website Development',
        icon: '🌐',
        description:
            'Fast, responsive, SEO-ready websites for businesses, schools and institutions — designed, built, hosted and maintained by us.',
        points: [
            'Business and corporate websites',
            'School and institute websites',
            'Landing pages and microsites',
            'SEO, analytics and maintenance',
        ],
    },
    {
        title: 'Mobile App Development',
        icon: '📱',
        description:
            'Android and iOS applications for staff, customers and parents — built to stay usable on modest hardware and patchy connections.',
        points: [
            'Android and iOS apps',
            'Staff and customer apps',
            'Push and WhatsApp notifications',
            'Play Store and App Store release',
        ],
    },
    {
        title: 'Custom Software & SaaS',
        icon: '⚙️',
        description:
            'Bespoke platforms for when off-the-shelf will not fit — admin portals, reporting dashboards and full multi-tenant SaaS products.',
        points: [
            'Admin and customer portals',
            'ERP dashboards',
            'Multi-tenant SaaS platforms',
            'Long-term feature development',
        ],
    },
    {
        title: 'E-commerce Development',
        icon: '🛒',
        description:
            'Online stores with catalogue, cart, payments and order management wired together — plus the operational tooling behind them.',
        points: [
            'Product catalogue and cart',
            'Payment gateway checkout',
            'Order and inventory management',
            'Delivery and dispatch tracking',
        ],
    },
    {
        title: 'Payments & Integrations',
        icon: '💳',
        description:
            'Payment collection built in from day one, backed by our payment partners, plus the API integrations that connect your systems together.',
        points: [
            'Online payment collection',
            'Automated receipts and invoices',
            'Third-party API integrations',
            'Reconciliation and settlement reports',
        ],
    },
    {
        title: 'WhatsApp & Communication Automation',
        icon: '💬',
        description:
            'Automated messaging that reaches people where they already are — invoices, reminders, alerts and confirmations delivered without manual effort.',
        points: [
            'WhatsApp API integration',
            'Fee and payment reminders',
            'Attendance and payroll alerts',
            'Bulk announcements',
        ],
    },
    {
        title: 'Cloud, Hosting & Support',
        icon: '☁️',
        description:
            'Secure cloud infrastructure with backups, monitoring and a support team that stays on after go-live — not just through the launch.',
        points: [
            'Managed cloud hosting',
            'Automated backups',
            'Secure authentication',
            'Training and ongoing support',
        ],
    },
];

export const onboardingSteps = [
    {
        step: '01',
        title: 'Discovery',
        description:
            'We map how your organization actually works today — the registers, the spreadsheets, the workarounds — before proposing anything.',
    },
    {
        step: '02',
        title: 'Setup & Migration',
        description:
            'Your platform is configured to your structure and your existing data is migrated in, verified and reconciled.',
    },
    {
        step: '03',
        title: 'Training',
        description:
            'Hands-on training for admins, staff and end users, with documentation and a named point of contact.',
    },
    {
        step: '04',
        title: 'Go Live & Support',
        description:
            'A phased rollout so nothing breaks mid-term, followed by continuous support, updates and new feature releases.',
    },
];

export const paymentPartners = [
    {
        name: 'Cashfree',
        role: 'Payments partner',
        note: 'Payment collection, payouts and settlement',
    },
    {
        name: 'Razorpay',
        role: 'Payments partner',
        note: 'Online payments, subscriptions and invoicing',
    },
];

export const capabilities = [
    'Multi-tenant SaaS infrastructure',
    'Secure authentication & role-based access',
    'Cloud hosting with automated backups',
    'Mobile-friendly across every platform',
    'WhatsApp and email automation',
    'Real-time dashboards and reporting',
    'Payment gateway integrations',
    'API-first architecture',
    'Ongoing support and phased rollouts',
];

export const stats = [
    { value: '5', label: 'SaaS platforms live' },
    { value: '2021', label: 'Operating since' },
    { value: '2', label: 'Payment partners' },
    { value: '24/7', label: 'Platform availability' },
];

export const values = [
    {
        title: 'Practical over flashy',
        description:
            'Software is judged on whether a clerk, a teacher or a technician can use it on a busy day — not on how it demos.',
        icon: '🎯',
    },
    {
        title: 'Built for Indian operations',
        description:
            'Fee cycles, salary advances, GST invoices, WhatsApp-first communication. Designed around how business is actually run here.',
        icon: '🇮🇳',
    },
    {
        title: 'Own the whole stack',
        description:
            'We build, host, integrate and support our platforms in-house, so accountability never gets passed to a third party.',
        icon: '🧱',
    },
    {
        title: 'Long-term partnerships',
        description:
            'Onboarding is the start, not the finish. Clients get continuous updates, new features and a team that picks up the phone.',
        icon: '🤝',
    },
];

export const retailHighlights = [
    'Grocery & daily essentials',
    'Fashion & apparel',
    'Electronics & gadgets',
    'Home & lifestyle',
];

export const franchiseBenefits = [
    {
        title: 'Proven retail model',
        description: 'Store layouts, category mix, pricing and operating systems already tested in live SuperMarts.',
        icon: '📐',
    },
    {
        title: 'Technology included',
        description: 'Billing, inventory, payroll and in-store digital signage run on our own platforms from day one.',
        icon: '🖥️',
    },
    {
        title: 'Exclusive territory',
        description: 'Protected catchment areas so franchise partners are not competing against each other.',
        icon: '📍',
    },
    {
        title: 'Setup support',
        description: 'Help with site evaluation, fit-out planning, vendor onboarding and opening stock.',
        icon: '🏗️',
    },
    {
        title: 'Training & operations',
        description: 'Staff training, standard operating procedures and continuing operational guidance.',
        icon: '🎓',
    },
    {
        title: 'Brand & marketing',
        description: 'Brand identity, launch campaigns and ongoing promotional material for your location.',
        icon: '📣',
    },
];

export const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy.html' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions.html' },
    { label: 'Refund & Returns Policy', href: '/refund-policy.html' },
    { label: 'Shipping Policy', href: '/shipping-policy.html' },
];

export const navLinks = [
    { label: 'Products', to: '/products' },
    { label: 'IT Services', to: '/services' },
    { label: 'Franchise', to: '/franchise' },
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
];

/** Builds a wa.me deep link from a record of form fields. */
export function whatsappLink(title: string, fields: Record<string, string>): string {
    const body = Object.entries(fields)
        .map(([key, value]) => `*${key}:* ${value.trim() || 'Not provided'}`)
        .join('\n');
    const message = `*${title}*\n\n${body}\n\n_Sent from kifayti.com_`;
    return `https://wa.me/${company.phoneRaw}?text=${encodeURIComponent(message)}`;
}
